import { BackendService, GameConfigService, RpcService } from '~/rage';
import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import {
	BarberShopItemConfig,
	BarberShopSectionConfig,
	Gender,
	IClientConfigs,
	IPedAppearanceValue,
	PedOverlaySection,
	WalletType,
} from '~/shared';
import { IBarberShopService } from './barberShopServiceTypes';

export class BarberShopService implements IBarberShopService {
	private items = new Subject<BarberShopItemConfig[]>([]);
	private currentAppearance = new Subject<IPedAppearanceValue | null>(null);
	private overlaysFemale = new Subject<
		IClientConfigs['appearance']['overlays']
	>([]);
	private overlaysMale = new Subject<IClientConfigs['appearance']['overlays']>(
		[],
	);
	private hairColors = new Subject<IClientConfigs['appearance']['hairColors']>(
		[],
	);
	private makeupColors = new Subject<
		IClientConfigs['appearance']['makeupColors']
	>([]);
	private sections = new Subject<BarberShopSectionConfig[]>([]);
	private seenTracker = new SeenTracker('barber_shop');

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly rpc: RpcService,
	) {
		this.gameConfig.getConfigs().subscribe(() => {
			this.fetchItems();
			this.fetchOverlays();
			this.fetchColors();
			this.fetchSections();
		});

		this.backend.addEventHandler(
			'appearance:update',
			(data: { appearance: IPedAppearanceValue }) => {
				this.currentAppearance.next(data.appearance);
			},
		);
	}

	public getOverlayConfigsByGender(
		gender: Gender,
	): IObservable<IClientConfigs['appearance']['overlays']> {
		return gender === Gender.FEMALE ? this.overlaysFemale : this.overlaysMale;
	}

	private fetchOverlays(): void {
		if (!this.gameConfig.configs) return;

		const overlays = this.gameConfig.configs.appearance?.overlays?.filter(
			(o) => o.isEnabled && o.isBarberShopEnabled,
		);

		const overlaysFemale =
			overlays?.filter((e) => e.gender !== Gender.MALE) ?? [];
		this.overlaysFemale.next(overlaysFemale);

		const overlaysMale =
			overlays?.filter((e) => e.gender !== Gender.FEMALE) ?? [];
		this.overlaysMale.next(overlaysMale);
	}

	private fetchColors(): void {
		if (!this.gameConfig.configs) return;

		const hairColors =
			this.gameConfig.configs.appearance?.hairColors?.filter(
				(o) => o.isEnabled && o.isBarberShopEnabled,
			) ?? [];
		this.hairColors.next(hairColors);

		const makeupColors =
			this.gameConfig.configs.appearance?.makeupColors?.filter(
				(o) => o.isEnabled && o.isBarberShopEnabled,
			) ?? [];
		this.makeupColors.next(makeupColors);
	}

	private fetchItems(): void {
		if (!this.gameConfig.configs) return;

		const configItems = this.gameConfig.configs.barberShop?.items ?? [];
		this.items.next(configItems as BarberShopItemConfig[]);
	}

	private fetchSections(): void {
		const sections = this.gameConfig.configs?.barberShop?.sections;
		if (sections) {
			this.sections.next(sections);
		}
	}

	public getItems(): IObservable<BarberShopItemConfig[]> {
		return this.items;
	}

	public getCurrentAppearance(): IObservable<IPedAppearanceValue | null> {
		return this.currentAppearance;
	}

	public getSections(): IObservable<BarberShopSectionConfig[]> {
		return this.sections;
	}

	public async buyItem(
		overlaySection: PedOverlaySection,
		index: number,
		primaryColorIndex: number,
		secondaryColorIndex: number,
		opacity: number,
		walletType: WalletType,
	): Promise<void> {
		await this.backend.invoke<void>('barberShop:buy', {
			overlaySection,
			index,
			primaryColorIndex,
			secondaryColorIndex,
			opacity,
			walletType,
		});
	}

	public async previewItem(
		overlaySection?: PedOverlaySection | null,
		index?: number,
		primaryColorIndex?: number,
		secondaryColorIndex?: number,
		opacity?: number,
	): Promise<void> {
		if (!overlaySection || index === undefined) {
			this.rpc.invokeClient('barberShop:clearPreview');
			return;
		}

		await this.rpc.invokeClient('barberShop:preview', {
			overlaySection,
			index,
			primaryColorIndex,
			secondaryColorIndex,
			opacity,
		});
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		this.seenTracker.markAsSeen(path);
	}

	public async exitMenu(): Promise<void> {
		await this.rpc.invokeClient('barberShop:exitMenu');
	}

	public getHairColors(): IObservable<
		IClientConfigs['appearance']['hairColors']
	> {
		return this.hairColors;
	}

	public getMakeupColors(): IObservable<
		IClientConfigs['appearance']['makeupColors']
	> {
		return this.makeupColors;
	}
}
